import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Heart, Sparkles, Award, Users, Globe, Zap, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { TEAM_AVATARS, TEAM_THEMES } from '../components/teamAvatars';
import AIMonitoringStrip from '../components/AIMonitoringStrip';

const content = {
  en: {
    eyebrow: 'ABOUT KITXENS',
    titleA: 'One Partner.',
    titleAccent: 'Every Restaurant Solution.',
    intro:
      'Kitxens was founded by restaurant operators and technologists who were tired of juggling five vendors to keep a single concept running. We built the cloud-based IT, AI and growth partner we wished existed.',
    sub:
      'Today we work with independent restaurants, multi-location concepts and growing hospitality groups worldwide — connecting their POS, ordering, marketing, AI and analytics into a single ecosystem.',
    talk: 'Talk to Our Team',
    explore: 'Explore Services',
    statsLabels: ['Managed Service', 'Dedicated Support', 'Bilingual Team', 'Integrations Connected'],
    missionEyebrow: 'OUR MISSION',
    missionTitleA: 'Free restaurant operators to do what they do best —',
    missionTitleAccent: 'hospitality.',
    missionBody:
      'Independent restaurants are the heart of every neighborhood, and yet operators spend far too much time wrestling with technology, vendors and admin work that doesn’t touch a guest. Our mission is to absorb that work — with a unified ecosystem, dedicated humans and AI that actually pays for itself.',
    missionFooter:
      'We believe great restaurants deserve great technology partners. That is what we are building, one concept at a time.',
    teamEyebrow: 'THE TEAM',
    teamTitle: 'Restaurant Operators. Technologists. Builders.',
    teamBody: 'A small, senior team obsessed with making restaurants run better.',
    humanLabel: 'Human Team',
    aiOpsEyebrow: 'AI OPERATING TEAM',
    aiOpsTitle: 'Six specialists on shift, 24 hours a day.',
    aiOpsBody: 'Every human on our team is amplified by a specialized AI operator. They handle the repetitive work — reservations, prospecting, follow-ups, content, contracts — so our people can focus on the judgment calls that actually move a restaurant forward.',
    togetherEyebrow: 'HUMANS + AI',
    togetherTitle: 'Working together, 24/7.',
    togetherBody: 'Our humans set the strategy, own the client relationship and make the calls that matter. Our AI operators execute the workload at scale, in every timezone, without ever going off shift. Together they deliver restaurant results no traditional agency can match.',
    togetherStats: [
      { k: '24/7', v: 'Continuous coverage across every timezone' },
      { k: '< 60s', v: 'Median response time to any guest inbound' },
      { k: '6', v: 'AI operators supporting every human on shift' },
      { k: '100%', v: 'Human judgment on every strategic decision' },
    ],
    ctaTitle: 'Let’s grow your restaurant together.',
    ctaBody: 'Book a free 30-minute audit. We’ll review your current stack, opportunities and a tailored growth plan.',
    ctaButton: 'Request Free Audit',
    serving: 'Helping restaurants grow worldwide.',
    values: [
      { title: 'Outcomes Over Output', text: 'We measure ourselves by the revenue, time and visibility we deliver, not by tickets closed.' },
      { title: 'Hospitality First', text: 'We are restaurant people. Every product and process is built around the realities of a working kitchen.' },
      { title: 'Built With AI', text: 'We use AI as a force multiplier so our team can solve the hardest problems for our clients.' },
      { title: 'Quality is Non-Negotiable', text: 'From responses to reporting, we hold ourselves to a hospitality-grade standard of craft.' },
    ],
    team: [
      { name: 'Carlos Reyes', role: 'Founder & CEO' },
      { name: 'Priya Patel', role: 'Head of Restaurant Success' },
      { name: 'Marcus Lee', role: 'Director of AI & Automation' },
      { name: 'Sofia Alvarez', role: 'Head of Growth' },
    ],
  },
  es: {
    eyebrow: 'SOBRE KITXENS',
    titleA: 'Un Solo Socio.',
    titleAccent: 'Toda Solución para Restaurantes.',
    intro:
      'Kitxens fue fundada por operadores de restaurantes y tecnólogos cansados de manejar cinco proveedores para mantener un solo concepto en marcha. Construimos el socio en la nube de IT, IA y crecimiento que siempre quisimos tener.',
    sub:
      'Hoy trabajamos con restaurantes independientes, conceptos multilocación y grupos de hospitalidad en crecimiento en Estados Unidos, conectando POS, pedidos, marketing, IA y analítica en un solo ecosistema.',
    talk: 'Habla con nuestro equipo',
    explore: 'Explorar Servicios',
    statsLabels: ['Servicio Gestionado', 'Soporte Dedicado', 'Equipo Bilingüe', 'Integraciones Conectadas'],
    missionEyebrow: 'NUESTRA MISIÓN',
    missionTitleA: 'Liberar a los operadores para que hagan lo que mejor saben hacer:',
    missionTitleAccent: 'hospitalidad.',
    missionBody:
      'Los restaurantes independientes son el corazón de cada barrio, pero los operadores gastan demasiado tiempo lidiando con tecnología, proveedores y trabajo administrativo que no toca al cliente. Nuestra misión es absorber ese trabajo con un ecosistema unificado, humanos dedicados e IA que se paga sola.',
    missionFooter:
      'Creemos que los grandes restaurantes merecen grandes socios tecnológicos. Eso es lo que estamos construyendo, un concepto a la vez.',
    teamEyebrow: 'EL EQUIPO',
    teamTitle: 'Operadores. Tecnólogos. Constructores.',
    teamBody: 'Un equipo pequeño y senior, obsesionado con hacer que los restaurantes funcionen mejor.',
    humanLabel: 'Equipo Humano',
    aiOpsEyebrow: 'EQUIPO OPERATIVO IA',
    aiOpsTitle: 'Seis especialistas de turno, 24 horas al día.',
    aiOpsBody: 'Cada persona del equipo humano está amplificada por un operador IA especializado. Se ocupan del trabajo repetitivo — reservas, prospección, follow-ups, contenido, contratos — para que nuestra gente se centre en las decisiones que realmente hacen crecer un restaurante.',
    togetherEyebrow: 'HUMANOS + IA',
    togetherTitle: 'Trabajando juntos, 24/7.',
    togetherBody: 'Nuestros humanos definen la estrategia, son dueños de la relación con el cliente y toman las decisiones que importan. Nuestros operadores IA ejecutan la carga de trabajo a escala, en cualquier zona horaria, sin salir nunca de turno. Juntos entregan resultados que ninguna agencia tradicional puede igualar.',
    togetherStats: [
      { k: '24/7', v: 'Cobertura continua en cada zona horaria' },
      { k: '< 60s', v: 'Tiempo mediano de respuesta a un cliente' },
      { k: '6', v: 'Operadores IA por cada humano de turno' },
      { k: '100%', v: 'Juicio humano en cada decisión estratégica' },
    ],
    ctaTitle: 'Hagamos crecer tu restaurante juntos.',
    ctaBody: 'Agenda una auditoría gratuita de 30 minutos. Revisaremos tu stack actual, oportunidades y un plan de crecimiento a medida.',
    ctaButton: 'Solicitar Auditoría Gratis',
    serving: 'Atendemos restaurantes en todo Estados Unidos.',
    values: [
      { title: 'Resultados sobre Tareas', text: 'Nos medimos por los ingresos, el tiempo y la visibilidad que entregamos, no por tickets cerrados.' },
      { title: 'Hospitalidad Primero', text: 'Somos gente de restaurantes. Cada producto y proceso está diseñado para la realidad de una cocina activa.' },
      { title: 'Hecho con IA', text: 'Usamos IA como multiplicador para que nuestro equipo resuelva los problemas más difíciles.' },
      { title: 'Calidad Innegociable', text: 'Desde respuestas a reportes, mantenemos un estándar de excelencia digno de la hospitalidad.' },
    ],
    team: [
      { name: 'Carlos Reyes', role: 'Fundador y CEO' },
      { name: 'Priya Patel', role: 'Líder de Éxito del Cliente' },
      { name: 'Marcus Lee', role: 'Director de IA y Automatización' },
      { name: 'Sofia Alvarez', role: 'Líder de Crecimiento' },
    ],
  },
};

const valueIcons = [Target, Heart, Sparkles, Award];
const stats = ['100%', '24/7', 'EN·ES', '60+'];
const photos = [
  'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=400&q=70',
];

export default function AboutUsPage() {
  const { lang } = useT();
  const c = content[lang] || content.en;
  return (
    <div className="pt-28">
      <SEO
        path="/aboutus"
        title="About Kitxens — Restaurant Operating Intelligence"
        description={c.intro}
      />
      <section className="relative bg-gradient-to-b from-orange-50/60 to-white pt-8 pb-14 md:py-16 lg:py-24">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-4">{c.eyebrow}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-bold text-neutral-900 leading-[1.05] tracking-[-0.02em]">
              {c.titleA} <span className="text-[#F77F11]">{c.titleAccent}</span>
            </h1>
            <p className="mt-6 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-xl">{c.intro}</p>
            <p className="mt-4 text-neutral-500 text-sm leading-relaxed max-w-xl">{c.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                className="bg-[#F77F11] hover:bg-[#d8651a] text-white px-6 h-12 rounded-md text-[15px] font-semibold"
              >
                <Link to="/contact#contact-form" data-testid="aboutus-cta-talk">
                  {c.talk} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-[#F77F11] text-[#F77F11] hover:bg-orange-50 hover:text-[#d8651a] px-6 h-12 rounded-md text-[15px] font-semibold"
              >
                <Link to="/services">{c.explore}</Link>
              </Button>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl border border-orange-100 aspect-[4/3]">
            <img
              src="https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/c6c3yz0p_kitxens%20oficina%20en%20SV.png"
              alt="Kitxens headquarters in Silicon Valley — the team behind Restaurant Operating Intelligence"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <AIMonitoringStrip />

      <section className="py-12 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl border border-neutral-200 bg-white p-6 text-center hover:border-[#F77F11] transition-colors"
            >
              <p className="text-3xl sm:text-4xl font-bold text-[#F77F11] tracking-[-0.02em]">{s}</p>
              <p className="text-sm text-neutral-600 mt-1">{c.statsLabels[i]}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <p className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-3">{c.missionEyebrow}</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
              {c.missionTitleA} <span className="text-[#F77F11]">{c.missionTitleAccent}</span>
            </h1>
            <p className="mt-6 text-neutral-300 leading-relaxed">{c.missionBody}</p>
            <p className="mt-4 text-neutral-400 text-sm leading-relaxed">{c.missionFooter}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {c.values.map((v, idx) => {
              const Icon = valueIcons[idx];
              return (
                <div
                  key={v.title}
                  className="rounded-2xl bg-neutral-900/70 border border-neutral-800 hover:border-[#F77F11] transition-colors p-5"
                >
                  <Icon className="w-6 h-6 text-[#F77F11] mb-3" />
                  <h3 className="font-semibold text-base mb-1">{v.title}</h3>
                  <p className="text-xs text-neutral-400 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-3">{c.teamEyebrow}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">{c.teamTitle}</h2>
            <p className="mt-3 text-neutral-600">{c.teamBody}</p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1">
              <Users className="w-3.5 h-3.5 text-[#F77F11]" />
              <span className="text-[11px] tracking-[0.14em] uppercase font-semibold text-neutral-700">{c.humanLabel}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {c.team.map((m, i) => (
              <div
                key={m.name}
                className="rounded-2xl overflow-hidden border border-neutral-100 bg-white hover:shadow-xl transition-shadow"
              >
                <div className="aspect-square bg-neutral-100">
                  <img src={photos[i]} alt={m.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 text-center">
                  <h3 className="font-semibold text-neutral-900">{m.name}</h3>
                  <p className="text-sm text-[#F77F11]">{m.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AI OPERATING TEAM ============ */}
      <section className="py-20 lg:py-24 bg-neutral-50" data-testid="aboutus-ai-team">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-3">{c.aiOpsEyebrow}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">{c.aiOpsTitle}</h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">{c.aiOpsBody}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
            {TEAM_AVATARS.map((a, i) => {
              const theme = TEAM_THEMES[i % TEAM_THEMES.length];
              return (
                <Link
                  key={a.key}
                  to={`/operating-intelligence#${a.key}`}
                  className="group flex flex-col items-center rounded-2xl border border-neutral-200 bg-white hover:border-[#F77F11] hover:shadow-lg transition-all p-4"
                  data-testid={`aboutus-ai-${a.key}`}
                >
                  <div className={`relative rounded-full p-[3px] bg-gradient-to-br ${theme.ring}`}>
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-white">
                      <img src={a.img} alt={a.name} className="w-full h-full object-cover" />
                    </div>
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white ${theme.dot}`} />
                  </div>
                  <p className="mt-3 font-bold text-neutral-900 text-[15px]">{a.name}</p>
                  <p className="text-[11px] tracking-[0.05em] uppercase text-neutral-500 mt-0.5 text-center">{a.role}</p>
                </Link>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/operating-intelligence"
              className="inline-flex items-center gap-1.5 text-[#F77F11] hover:text-[#d8651a] font-semibold text-sm"
              data-testid="aboutus-explore-ai-team"
            >
              {lang === 'es' ? 'Explora el Equipo Operativo' : 'Explore the Operating Team'} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ HUMANS + AI TOGETHER ============ */}
      <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden" data-testid="aboutus-together">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 22% 28%, #F77F11 1px, transparent 1.6px), radial-gradient(circle at 78% 72%, #F77F11 1px, transparent 1.6px)',
            backgroundSize: '120px 120px, 170px 170px',
          }}
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-3">{c.togetherEyebrow}</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.015em]">{c.togetherTitle}</h2>
              <p className="mt-5 text-neutral-300 leading-relaxed">{c.togetherBody}</p>
              <div className="mt-8 flex items-center gap-4 text-sm text-neutral-400">
                <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-[#F77F11]" /> {c.humanLabel}</span>
                <span className="text-neutral-600">+</span>
                <span className="inline-flex items-center gap-2"><Zap className="w-4 h-4 text-[#F77F11]" /> {c.aiOpsEyebrow}</span>
                <span className="text-neutral-600">=</span>
                <span className="inline-flex items-center gap-2 text-white font-semibold"><Clock className="w-4 h-4 text-[#F77F11]" /> 24/7</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {c.togetherStats.map((s) => (
                <div
                  key={s.k}
                  className="rounded-2xl bg-neutral-900/70 border border-neutral-800 hover:border-[#F77F11] transition-colors p-5"
                >
                  <p className="text-2xl sm:text-3xl font-bold text-[#F77F11] tracking-[-0.02em]">{s.k}</p>
                  <p className="text-xs text-neutral-400 leading-relaxed mt-1">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-white to-orange-50/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Users className="w-10 h-10 text-[#F77F11] mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900">{c.ctaTitle}</h2>
          <p className="mt-4 text-neutral-600">{c.ctaBody}</p>
          <Button
            asChild
            className="mt-7 bg-[#F77F11] hover:bg-[#d8651a] text-white px-6 h-12 rounded-md text-[15px] font-semibold"
          >
            <Link to="/#audit" data-testid="aboutus-cta-audit">
              {c.ctaButton} <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-neutral-500">
            <Globe className="w-4 h-4 text-[#F77F11]" /> {c.serving}
          </div>
        </div>
      </section>
    </div>
  );
}
